import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../lib/utils';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
  separator?: React.ReactNode;
  renderLink?: (props: {
    href: string;
    children: React.ReactNode;
    className: string;
  }) => React.ReactNode;
}

export function Breadcrumb({
  items,
  className,
  showHome = false,
  separator,
  renderLink,
}: BreadcrumbProps) {
  const separatorNode = separator ?? (
    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-subtle" />
  );

  const linkClass =
    'inline-flex items-center gap-1.5 font-body text-body-sm text-text-muted transition-interactive duration-200 hover:text-statura-green-accessible';
  const activeClass =
    'inline-flex items-center gap-1.5 font-body text-body-sm font-medium text-text-heading';

  const defaultRenderLink = (props: {
    href: string;
    children: React.ReactNode;
    className: string;
  }) => (
    <a href={props.href} className={props.className}>
      {props.children}
    </a>
  );

  const linkRenderer = renderLink ?? defaultRenderLink;

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center gap-2">
        {showHome && (
          <li className="flex items-center gap-2">
            {linkRenderer({
              href: '/',
              className: linkClass,
              children: <Home className="h-4 w-4" />,
            })}
            {items.length > 0 && separatorNode}
          </li>
        )}
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={idx} className="flex items-center gap-2">
              {isLast || !item.href ? (
                <span className={isLast ? activeClass : linkClass}>
                  {item.icon && (
                    <span className="shrink-0 [&_svg]:h-4 [&_svg]:w-4">{item.icon}</span>
                  )}
                  {item.label}
                </span>
              ) : (
                linkRenderer({
                  href: item.href,
                  className: linkClass,
                  children: (
                    <>
                      {item.icon && (
                        <span className="shrink-0 [&_svg]:h-4 [&_svg]:w-4">{item.icon}</span>
                      )}
                      {item.label}
                    </>
                  ),
                })
              )}
              {!isLast && separatorNode}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
